import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi, classApi } from '../lib/api';
import { 
  ArrowLeft, Plus, Save, Loader2, BookOpen, Trash2
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

export function AdminExamCreate() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [examData, setExamData] = useState({
    title: '',
    titleBn: '',
    classId: 9,
    subjectId: 25,
    chapterId: undefined as number | undefined,
    type: 'practice' as 'live' | 'practice',
    durationMinutes: 30,
    totalMarks: 50,
    passMarks: 25,
    isPremium: false,
    price: 0,
  });
  const [questions, setQuestions] = useState([{
    questionText: '',
    questionTextBn: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: 'a' as 'a' | 'b' | 'c' | 'd',
    explanation: '',
    marks: 1,
  }]);

  const { data: classes } = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await classApi.getClasses();
      return res.data;
    },
  });

  const { data: subjects } = useQuery({
    queryKey: ['subjects', examData.classId],
    queryFn: async () => {
      const res = await classApi.getSubjects(examData.classId);
      return res.data;
    },
    enabled: !!examData.classId,
  });

  const addQuestion = () => {
    setQuestions([...questions, {
      questionText: '',
      questionTextBn: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctOption: 'a',
      explanation: '',
      marks: 1,
    }]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Create exam
      const examRes = await adminApi.createExam(examData);
      const examId = examRes.data.id;

      // Add questions
      if (questions.length > 0 && questions[0].questionText) {
        await adminApi.addQuestions(examId, questions);
      }

      toast.success('Exam created successfully!');
      navigate('/admin');
    } catch (error) {
      toast.error('Failed to create exam');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const allClasses = classes || [];
  const allSubjects = subjects || [];

  return (
    <div className="space-y-6">
      <FadeIn>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Create Exam</h1>
            <p className="text-slate-500">Add a new exam with questions</p>
          </div>
        </div>
      </FadeIn>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Exam Details */}
        <FadeIn>
          <AnimatedCard className="p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary-600" />
              Exam Details
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title (English) *</label>
                <input
                  type="text"
                  value={examData.title}
                  onChange={(e) => setExamData({ ...examData, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  placeholder="Physics Chapter 1 Test"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title (Bengali)</label>
                <input
                  type="text"
                  value={examData.titleBn}
                  onChange={(e) => setExamData({ ...examData, titleBn: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  placeholder="পদার্থবিজ্ঞান অধ্যায় ১ পরীক্ষা"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Class *</label>
                <select
                  value={examData.classId}
                  onChange={(e) => setExamData({ ...examData, classId: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  required
                >
                  {allClasses.map((cls: any) => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                <select
                  value={examData.subjectId}
                  onChange={(e) => setExamData({ ...examData, subjectId: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  required
                >
                  {allSubjects.map((sub: any) => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (minutes) *</label>
                <input
                  type="number"
                  value={examData.durationMinutes}
                  onChange={(e) => setExamData({ ...examData, durationMinutes: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  min={5}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Total Marks *</label>
                <input
                  type="number"
                  value={examData.totalMarks}
                  onChange={(e) => setExamData({ ...examData, totalMarks: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  min={1}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pass Marks *</label>
                <input
                  type="number"
                  value={examData.passMarks}
                  onChange={(e) => setExamData({ ...examData, passMarks: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  min={1}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Exam Type</label>
                <select
                  value={examData.type}
                  onChange={(e) => setExamData({ ...examData, type: e.target.value as 'live' | 'practice' })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                >
                  <option value="practice">Practice</option>
                  <option value="live">Live</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPremium"
                    checked={examData.isPremium}
                    onChange={(e) => setExamData({ ...examData, isPremium: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded border-slate-300"
                  />
                  <label htmlFor="isPremium" className="text-sm text-slate-700">Premium Exam</label>
                </div>
                {examData.isPremium && (
                  <input
                    type="number"
                    value={examData.price}
                    onChange={(e) => setExamData({ ...examData, price: parseInt(e.target.value) || 0 })}
                    className="px-3 py-1 rounded-lg border border-slate-300 text-sm w-24"
                    placeholder="Price"
                  />
                )}
              </div>
            </div>
          </AnimatedCard>
        </FadeIn>

        {/* Questions */}
        <FadeIn>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">Questions ({questions.length})</h2>
            <button
              type="button"
              onClick={addQuestion}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Question
            </button>
          </div>
        </FadeIn>

        {questions.map((q, index) => (
          <FadeIn key={index} delay={index * 0.05}>
            <AnimatedCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900">Question {index + 1}</h3>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(index)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Question (English) *</label>
                  <input
                    type="text"
                    value={q.questionText}
                    onChange={(e) => updateQuestion(index, 'questionText', e.target.value)}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    placeholder="What is the SI unit of length?"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Question (Bengali)</label>
                  <input
                    type="text"
                    value={q.questionTextBn}
                    onChange={(e) => updateQuestion(index, 'questionTextBn', e.target.value)}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    placeholder="দৈর্ঘ্যের এসআই একক কী?"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  {['A', 'B', 'C', 'D'].map((opt) => (
                    <div key={opt}>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Option {opt} *</label>
                      <input
                        type="text"
                        value={(q as any)[`option${opt}`]}
                        onChange={(e) => updateQuestion(index, `option${opt}`, e.target.value)}
                        className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                        required
                      />
                    </div>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Correct Answer *</label>
                    <select
                      value={q.correctOption}
                      onChange={(e) => updateQuestion(index, 'correctOption', e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    >
                      <option value="a">Option A</option>
                      <option value="b">Option B</option>
                      <option value="c">Option C</option>
                      <option value="d">Option D</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Marks *</label>
                    <input
                      type="number"
                      value={q.marks}
                      onChange={(e) => updateQuestion(index, 'marks', parseInt(e.target.value))}
                      className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      min={1}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Explanation</label>
                  <input
                    type="text"
                    value={q.explanation}
                    onChange={(e) => updateQuestion(index, 'explanation', e.target.value)}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    placeholder="Explanation for the correct answer"
                  />
                </div>
              </div>
            </AnimatedCard>
          </FadeIn>
        ))}

        {/* Submit */}
        <FadeIn>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {saving ? 'Creating...' : 'Create Exam'}
            </button>
            <Link
              to="/admin"
              className="px-8 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
            >
              Cancel
            </Link>
          </div>
        </FadeIn>
      </form>
    </div>
  );
}
